import { Injectable } from '@angular/core';
import { ICustomerInfo, IPurchaseCriteria } from '../state/customer-state.service';
import { CalculatorService } from './calculator.service';
import { OrganizationService } from './organization.service';
import { AuthService } from './auth.service';
import * as Parse from 'parse';

@Injectable()
export class CustomerService {
    constructor(
        private calculator: CalculatorService,
        private orgService: OrganizationService,
        private auth: AuthService
    ) {
    }

    fetchCustomers(pageIndex: number, pageSize: number) {
        const query: Parse.Query = new Parse.Query('Customer');

        query.equalTo('organization', this.orgService.organization);
        query.limit(pageSize);
        query.skip(pageIndex * pageSize);
        query.descending('createdAt');

        query.withCount();

        return query.find();
    }

    searchByNameOrEmail(searchTerm: string, pageIndex: number, pageSize: number) {
        const keyword = searchTerm.toLowerCase();

        const nameQuery: Parse.Query = new Parse.Query('Customer');
        nameQuery.equalTo('organization', this.orgService.organization);
        nameQuery.contains('name', keyword);

        const emailQuery: Parse.Query = new Parse.Query('Customer');
        emailQuery.equalTo('organization', this.orgService.organization);
        emailQuery.contains('email', keyword);

        const query: Parse.Query = Parse.Query.or(nameQuery, emailQuery);
        query.limit(pageSize);
        query.skip(pageIndex * pageSize);

        query.withCount();

        return query.find();
    }

    fetchWeeklyReport(userType: string) {
        const today = new Date();

        let lastWeek = today.getWeek() - 1;
        // Last ISO week is 53, if ISO week is less than 1 go back to 53
        if (lastWeek < 1) {
            lastWeek = 53;
        }

        const q = new Parse.Query('CustomerReport');

        if (userType !== 'Admin') {
            q.equalTo('owner', Parse.User.current());
        }

        q.equalTo('year', today.getFullYear());
        q.equalTo('week', lastWeek);
        return q.find();
    }

    async fetchAgentNames(isAdmin: boolean, customer: Parse.Object) {
        if (isAdmin) {
            const query: Parse.Query<Parse.User> = new Parse.Query(Parse.User);

            query.equalTo('organization', customer.get('organization'));
            query.containedIn('type', ['Admin', 'AA', 'SA']);

            return query.find();
        }

        const ids = customer.get('requestingAgents').map((agent: Parse.Object) => agent.id);

        return Parse.Cloud.run('getAgentName', { ids });
    }

    checkUpdateToken(token: string) {
        return Parse.Cloud.run('checkUpdateToken', { token: token });
    }

    setupNewCustomer() {
        const customer = new Parse.Object('Customer');

        customer.set('organization', new Parse.Object('Organization', { objectId: this.orgService.organization.id }));
        customer.set('regions', []);
        customer.set('deliverSMS', true);
        customer.set('deliverEmail', true);

        return customer;
    }

    fetchCustomerById(id: string) {
        const customer = new Parse.Object('Customer');
        customer.id = id;

        return customer.fetch();
    }

    setCustomerValues(customer: Parse.Object, info: ICustomerInfo, purchaseCriteria: IPurchaseCriteria) {
        customer.set('name', info.name.trim().toLowerCase());
        customer.set('street', info.street.trim().toLowerCase());
        customer.set('city', info.city.trim().toLowerCase());
        customer.set('state', info.state.trim().toLowerCase());
        customer.set('zip', info.zip.trim().toLowerCase());
        customer.set('email', info.email.trim().toLowerCase());
        customer.set('phone', info.phone.trim().replace(/\D+/g, ''));
        customer.set('notes', info.notes.trim());
        customer.set('deliverEmail', info.deliverEmail);
        customer.set('deliverSMS', info.deliverSMS);

        customer.set('priceMin', this.calculator.dollarStringToNumber(purchaseCriteria.priceMin));
        customer.set('priceMax', this.calculator.dollarStringToNumber(purchaseCriteria.priceMax));
        customer.set('returnMin', this.calculator.dollarStringToNumber(purchaseCriteria.returnMin));
        customer.set('roiMin', this.calculator.percentStringToNumber(purchaseCriteria.roiMin));
        customer.set('wantsRental', purchaseCriteria.wantsRental);
        customer.set('wantsFlip', purchaseCriteria.wantsFlip);
        customer.set('wantsLand', purchaseCriteria.wantsLand);
    }

    setNewCustomerValues(customer: Parse.Object) {
        customer.set('preferredAgent', this.auth.currentUser);
        customer.set('deliverEmail', true);
        customer.set('deliverSMS', true);
        customer.set('confirmed', true);
    }

    setPreferredAgent(customer: Parse.Object, info: ICustomerInfo) {
        const prefAgent: Parse.User = new Parse.User();
        prefAgent.id = info.preferredAgent as string;

        customer.set('preferredAgent', prefAgent);
        customer.addUnique('requestingAgents', prefAgent);
    }

    sendUpdateInfoEmail(customerId: string) {
        return Parse.Cloud.run('sendUpdateInfoEmail', { customerId: customerId });
    }

    publicCreateCustomer(agentSlug: string, customer: Parse.Object) {
        return Parse.Cloud.run('publicCreateCustomer', {
            agentSlug: agentSlug,
            customer: customer.toJSON(),
            organizationId: this.orgService.organization.id
        });
    }

    saveCustomerWithToken(token: string, customer: Parse.Object, info: ICustomerInfo) {
        return Parse.Cloud.run('saveCustomerWithToken', {
            token: token,
            customer: {
                name: customer.get('name'),
                street: customer.get('street'),
                city: customer.get('city'),
                state: customer.get('state'),
                zip: customer.get('zip'),
                email: customer.get('email'),
                phone: customer.get('phone'),
                priceMin: customer.get('priceMin'),
                priceMax: customer.get('priceMax'),
                returnMin: customer.get('returnMin'),
                roiMin: customer.get('roiMin'),
                wantsRental: customer.get('wantsRental'),
                wantsFlip: customer.get('wantsFlip'),
                wantsLand: customer.get('wantsLand'),
                deliverEmail: customer.get('deliverEmail'),
                deliverSMS: customer.get('deliverSMS')
            },
            preferredAgent: info.preferredAgent,
            customerId: customer.id
        });
    }

    getPreferredAgent(id: string) {
        return Parse.Cloud.run('getPreferredAgent', {
            customerId: id
        });
    }

    getCustomerPhone(id: string) {
        return Parse.Cloud.run('getCustomerPhone', {
            customerId: id
        });
    }

    updateCustomer(id: string, name: string, phone: string) {
        return Parse.Cloud.run('updateCustomer', {
            customerId: id,
            name: name.trim().toLowerCase(),
            phone: phone.trim().replace(/\D+/g, '')
        });
    }

    confirmCustomerEmail(token: string) {
        return Parse.Cloud.run('confirmCustomerEmail', { token });
    }

    updatePreferredAgent(token: string, newAgentId: string) {
        return Parse.Cloud.run('updatePreferredAgent', { token, newAgent: newAgentId });
    }

    unsubscribeCustomer(email: string) {
        return Parse.Cloud.run('unsubscribeCustomer', {
            email: email.trim().toLowerCase(),
            organizationId: this.orgService.organization.id
        });
    }

    fetchCustomerTransfers(pageIndex: number, pageSize: number) {
        const query: Parse.Query<Parse.Object> = new Parse.Query('CustomerTransfer');

        query.equalTo('organization', this.orgService.organization);
        query.limit(pageSize);
        query.skip(pageIndex * pageSize);
        query.descending('createdAt');

        query.withCount();

        return query.find();
    }

    fetchCustomerImports(pageIndex: number, pageSize: number) {
        const query: Parse.Query<Parse.Object> = new Parse.Query('CustomerImport');

        query.equalTo('organization', this.orgService.organization);
        query.limit(pageSize);
        query.skip(pageIndex * pageSize);
        query.descending('createdAt');

        query.withCount();

        return query.find();
    }

    fetchCustomersByImportRecord(importRecord: Parse.Object, pageIndex: number, pageSize: number) {
        const query: Parse.Query<Parse.Object> = new Parse.Query('Customer');

        query.equalTo('organization', this.orgService.organization);
        query.equalTo('importRecord', importRecord);
        query.limit(pageSize);
        query.skip(pageIndex * pageSize);

        query.withCount();

        return query.find();
    }

    fetchCustomerHistory(customer: Parse.Object, pageIndex: number, pageSize: number) {
        const query: Parse.Query<Parse.Object> = new Parse.Query('CustomerHistory');

        query.equalTo('organization', this.orgService.organization);
        query.equalTo('customer', customer);
        query.descending('createdAt');
        query.limit(pageSize);
        query.skip(pageIndex * pageSize);

        query.withCount();

        return query.find();
    }

    publicGetCustomer(customerId: string) {
        return Parse.Cloud.run('publicGetCustomer', { id: customerId });
    }
}
